import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  FormAutocomplete,
  LabeledAutocomplete,
  formClasses,
  labelClasses,
} from "../styles/AutocompleteStyles";
import { TextField } from "@mui/material";
import { responseType } from "../utils/formTypes";
import { useLazyQuery } from "@apollo/client";

import useUrlParams from "../customHooks/useUrlParams";
import {
  EMPTY_STRING,
  FETCH_MORE_DATA,
  ROWS_PER_PAGE,
  debouncedDelay,
} from "../utils/constants";
import { OrderDirection } from "../utils/commonQueryTypes";
import { getModifiedScrollHeight } from "../utils/UtilFunctions";

import { GetItems } from "../components/modules/asset/masters/items/queries/queries";
import {
  AtsStckItemEdge,
  AtsStckItemOrderField,
  GetItemsResult,
  GetItemsVars,
  ItemQueryType,
} from "../components/modules/asset/masters/items/queries/types";
import { itmType } from "../components/modules/asset/purchase/material/statesProvider/materialContext";
import { StockEntryContext } from "../components/modules/asset/purchase/stockentry/Add/context/context";
import { useDebounce } from "../customHooks/useDebounce";

export interface ItemsAutocompleteProps {
  onChange: (newValue: itmType, uom: AtsStckItemEdge | undefined) => void;
  value: number;
  onKeyDown:
    | ((
        event: React.KeyboardEvent<HTMLDivElement> & {
          defaultMuiPrevented?: boolean;
        }
      ) => void)
    | undefined;
  placeholder: string;

  autoFocus?: boolean;

  QueryType?: ItemQueryType;
  label?: string;
  isComponent?: boolean;

  filterIds?: number[];
}
const ItemsAutocomplete = ({
  QueryType,
  onChange,
  onKeyDown,
  value,
  autoFocus,
  label,
  placeholder,
  isComponent,
  filterIds,
}: ItemsAutocompleteProps) => {
  const { customer_id, company_id } = useUrlParams();
  const [searchData, setSearchData] = useState("");
  const debouncedSearch = useDebounce(searchData, debouncedDelay);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [items, setItems] = useState<AtsStckItemEdge[]>([]);
  const [itemOptions, setItemOptions] = useState<itmType[]>([]);
  const [autoValue, setAutoValue] = useState<itmType | null>(null);

  const { state } = useContext(StockEntryContext);

  const [GetItms, { data, loading, fetchMore }] = useLazyQuery<
    GetItemsResult,
    GetItemsVars
  >(GetItems);

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetItems.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = items.find(
            (employee) => employee.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setItems(updatedNewData);
        setItemOptions(
          updatedNewData.map(({ node }) => ({
            label: node.desc,
            value: node.id,
            code: node.code,
          }))
        );
      } else {
        setItems(newData);
        setItemOptions(
          newData.map(({ node }) => ({
            label: node.desc,
            value: node.id,
            code: node.code,
          }))
        );
      }
      setEndCursor(data.GetItems.pageInfo.endCursor);
    }
    // eslint-disable-next-line
  }, [data, loading]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (customer_id) {
          GetItms({
            variables: {
              after: null,
              customerID: customer_id ? customer_id : 0,
              companyID: company_id ? company_id : 0,
              first: ROWS_PER_PAGE,
              orderBy: {
                direction: OrderDirection.ASC,
                field: AtsStckItemOrderField.DESC,
              },

              name: debouncedSearch,
              hsnCode: EMPTY_STRING,
              input: {
                query_type: QueryType ? QueryType : ItemQueryType.All,
                stckCategoryID: 0,
                stckGroupID: 0,
                stckManufacturerID: 0,
                stckUomID: 0,
                stckSubCategoryID: 0,
                ldgrID: 0,
                subsidiaryID: 0,
                isComponent,
              },
            },
          });
        }
      } catch (err) {
        console.error((err as Error).message);
      }
    };
    fetchData();
  }, [customer_id, GetItms, debouncedSearch, isComponent, QueryType]);

  useEffect(() => {
    if (value) {
      const foundValue = itemOptions.find(
        ({ value: optionsValue }) => Number(optionsValue) === Number(value)
      );

      setAutoValue(foundValue ? foundValue : null);
    } else {
      setAutoValue(null);
    }
  }, [value, itemOptions]);

  const handleScroll = (e: React.UIEvent<HTMLUListElement, UIEvent>) => {
    const target = e.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
      if (hasNextPage && !loading) {
        fetchMore({
          variables: {
            first: FETCH_MORE_DATA,
            after: endCursor,
          },
          updateQuery: (prevResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prevResult;

            const newEdges = fetchMoreResult.GetItems.edges;
            const pageInfo = fetchMoreResult.GetItems.pageInfo;
            setEndCursor(pageInfo.endCursor);
            setHasNextPage(pageInfo.hasNextPage);

            const duplicateCheck = prevResult.GetItems.edges.filter(
              ({ node: { id } }) =>
                newEdges.findIndex(
                  ({ node: { id: newId } }) => newId === id
                ) !== -1
            );

            if (duplicateCheck.length > 0) return prevResult;

            return {
              GetItems: {
                edges: [...items, ...newEdges],
                pageInfo,
                totalCount: data ? data.GetItems.totalCount! : 0,
              },
            };
          },
        });
      }
    }
  };

  let commonAutocompleteClassName = label
    ? labelClasses.inputRoot
    : formClasses.inputRoot;
  let commonTextFieldClassName = label
    ? labelClasses.formControlRoot
    : formClasses.formControlRoot;

  const Autocomplete = label ? LabeledAutocomplete : FormAutocomplete;
  const filteredOptions = useMemo(() => {
    if (filterIds) {
      return itemOptions.filter(({ value }) => !filterIds.includes(value));
    } else {
      return itemOptions;
    }
  }, [itemOptions, filterIds]);

  return (
    <Autocomplete
      className={commonAutocompleteClassName}
      options={filteredOptions}
      onKeyDown={onKeyDown}
      value={autoValue}
      openOnFocus
      forcePopupIcon
      getOptionLabel={(option) => {
        if ((option as itmType).code) {
          return `${(option as itmType).label}(${(option as itmType).code})`;
        } else {
          return `${(option as itmType).label}`;
        }
      }}
      slotProps={{
        listbox: {
          onScroll: handleScroll,
        },
      }}
      onChange={(e, newValue) => {
        const uom = items.find(
          ({ node }) =>
            newValue &&
            Number(node.id) === Number((newValue as responseType).value)
        );
        onChange(newValue as itmType, uom);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          autoFocus={autoFocus}
          onChange={(e) => setSearchData(e.target.value)}
          className={commonTextFieldClassName}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          placeholder={placeholder}
        />
      )}
    />
  );
};

export default ItemsAutocomplete;
